import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import * as queryString from 'query-string';

import { UnauthenticatedUserHandler } from '~src/authentication/services/UnauthenticatedUserHandler';
import { ITagResult } from '~src/httpClient/serverApi/types/IResponse';
import { ITagError } from '~src/httpClient/serverApi/types/ITagError';
import HttpStatusCode from '~src/httpClient/utils/HttpStatusCode';
import { ErrorReason, getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';


export interface ITagApiRequestConfig<TRequestBody extends Object = {}, TQueryParams extends Object = {}> {
  httpMethod: Method;
  endpoint: string;
  requestBody?: TRequestBody;
  queryParams?: TQueryParams;
}

export async function makeTagApiRequest<TResult extends ITagResult, TRequestBody extends Object = {}, TQueryParams extends Object = {}>(
  config: ITagApiRequestConfig<TRequestBody, TQueryParams>
): Promise<TResult> {
  let url = config.endpoint;
  if (config.queryParams) {
    url += `?${queryString.stringify(config.queryParams)}`;
  }

  const axiosConfig: AxiosRequestConfig = {
    method: config.httpMethod,
    url,
    data: config.requestBody
  };

  return getSessionManagementServiceInstance().sendRequestWithinSession(
    () => makeAxiosRequest<TResult>(axiosConfig)
  );
}

function makeAxiosRequest<TResult extends ITagResult>(requestConfig: AxiosRequestConfig): Promise<TResult> {
  return axios(requestConfig).then(
    (successResponse: AxiosResponse<TResult>) => {
      return successResponse.data;
    },
    (errorResponse: AxiosError<ITagError>) => {
      if (errorResponse.response.status === HttpStatusCode.Unauthorized) {
        const reason = errorResponse.response.data.httpErrorCode;
        UnauthenticatedUserHandler.handle401(reason);
        return Promise.reject(reason || ErrorReason.Unknown);
      }
      return {
        errors: [ errorResponse.response.data ]
      } as TResult;
    }
  );
}
